import { memo, useState } from 'react';
import OptimizedImage from '../optimized-image';
import MediaActionButtons from '../media-action-buttons';
import AuthModal from '../auth-modal';
import { getAllPosterSources, getAllBackdropSources } from '../../utils/poster-fallbacks';
import { useLanguage } from '../../contexts/language-context';
import useGoBack from '../../hooks/use-go-back';

const MovieHeader = memo(({
  movie,
  streamingProviders,
  hasExternalAvailability,
  streamingLookupResolved = true,
  loading = false,
}) => {
  const goBack = useGoBack('/');
  const { t } = useLanguage();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  const handleRequireAuth = () => {
    setAuthModalMode('register');
    setShowAuthModal(true);
  };

  const formatRuntime = (minutes) => {
    if (!minutes) return 'Nenustatyta';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}val ${mins}min`;
  };

  const getGenreNames = (genres) => {
    const genreMap = {
      28: 'Veiksmo', 12: 'Nuotykių', 16: 'Animacijos', 35: 'Komedijos',
      80: 'Kriminalinis', 99: 'Dokumentinis', 18: 'Drama', 10751: 'Šeimos',
      14: 'Fantastinis', 36: 'Istorinis', 27: 'Siaubo', 10402: 'Muzikinis',
      9648: 'Misterija', 10749: 'Romantinis', 878: 'Mokslinė fantastika', 10770: 'TV filmas',
      53: 'Trileris', 10752: 'Karo', 37: 'Vesternų'
    };
    return genres?.map(genre => genreMap[genre.id] || genre.name) || [];
  };

  // Get the best available poster source using our enhanced fallback system
  const getBestPosterSource = () => {
    const allSources = getAllPosterSources(movie);
    return allSources.length > 0 ? allSources[0] : null;
  };

  // Get the best available backdrop source
  const getBestBackdropSource = () => {
    const allSources = getAllBackdropSources(movie);
    return allSources.length > 0 ? allSources[0] : null;
  };

  const getRatingData = () => {
    const data = {
      imdb: null,
      rt: null,
      tmdb: null,
      hasRealData: false
    };

    if (movie?.enhanced_ratings?.real_ratings) {
      data.hasRealData = true;
      if (movie.enhanced_ratings.real_ratings.imdb?.rating) {
        data.imdb = movie.enhanced_ratings.real_ratings.imdb.rating.toFixed(1);
      }
      if (movie.enhanced_ratings.real_ratings.rottenTomatoes?.rating) {
        data.rt = movie.enhanced_ratings.real_ratings.rottenTomatoes.rating;
      }
    }
    
    const hasImdb = data.imdb !== null && data.imdb !== undefined;
    const hasRt = data.rt !== null && data.rt !== undefined;
    
    if (!hasImdb && !hasRt && movie?.vote_average && movie.vote_average > 0) {
      data.tmdb = movie.vote_average.toFixed(1);
    }

    return data;
  };

  if (loading) {
    return (
      <div className="relative">
        {/* Backdrop Skeleton */}
        <div className="absolute inset-0 z-0">
          <div className="w-full h-96 bg-dark-700 animate-pulse" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/70 to-dark-900/30" />
        </div>

        {/* Content Skeleton */}
        <div className="relative z-10 pt-8 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster Skeleton */}
              <div className="flex-shrink-0">
                <div className="w-80 max-w-full aspect-[2/3] bg-dark-700 rounded-xl animate-pulse" />
              </div>

              {/* Movie Info Skeleton */}
              <div className="flex-1 space-y-4">
                <div className="h-12 bg-dark-700 rounded-lg w-3/4 animate-pulse" />
                <div className="h-6 bg-dark-700 rounded-lg w-1/2 animate-pulse" />
                <div className="flex gap-4">
                  <div className="h-8 bg-dark-700 rounded-lg w-24 animate-pulse" />
                  <div className="h-8 bg-dark-700 rounded-lg w-24 animate-pulse" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-dark-700 rounded-lg w-full animate-pulse" />
                  <div className="h-4 bg-dark-700 rounded-lg w-5/6 animate-pulse" />
                  <div className="h-4 bg-dark-700 rounded-lg w-4/6 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced professional poster fallback component
  const PosterFallback = () => (
    <div className="w-80 h-120 rounded-xl shadow-2xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex flex-col items-center justify-center border border-gray-600 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 0%, transparent 50%), 
                           radial-gradient(circle at 75% 75%, rgba(139, 69, 19, 0.3) 0%, transparent 50%)`,
          backgroundSize: '60% 60%, 40% 40%'
        }} />
      </div>
      
      <div className="p-8 text-center relative z-10">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 12c.83 0 1.5-.67 1.5-1.5S9.83 9 9 9s-1.5.67-1.5 1.5S8.17 12 9 12zm3 5L9 14l-3 4h12l-4.5-6L12 17z"/>
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-200 mb-2 line-clamp-2">{movie.title}</h3>
        <p className="text-xs text-gray-500">{movie.release_date?.split('-')[0] || 'N/A'}</p>
      </div>
    </div>
  );

  const bestBackdropSource = getBestBackdropSource();

  return (
    <>
      <div className="relative">
        {/* Backdrop or Professional Fallback Background */}
        <div className="absolute inset-0 z-0">
          {bestBackdropSource ? (
            <>
              <img
                src={bestBackdropSource}
                alt={movie.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/70 to-dark-900/30" />
            </>
          ) : (
            /* Professional gradient background when no backdrop exists */
            <div className="w-full h-full bg-gradient-to-br from-gray-900 via-dark-900 to-gray-800">
              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 opacity-20">
                <div className="w-full h-full" style={{
                  backgroundImage: `radial-gradient(circle at 25% 25%, rgba(139, 69, 19, 0.3) 0%, transparent 50%), 
                                   radial-gradient(circle at 75% 75%, rgba(30, 58, 138, 0.3) 0%, transparent 50%),
                                   radial-gradient(circle at 50% 50%, rgba(75, 85, 99, 0.2) 0%, transparent 50%)`,
                  backgroundSize: '100% 100%, 80% 80%, 60% 60%'
                }} />
              </div>
              {/* Additional gradient overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/60 to-dark-900/20" />
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="relative z-10 pt-8 pb-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={goBack}
              className="inline-flex items-center space-x-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
              aria-label={t('goBack')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span>{t('goBack')}</span>
            </button>
          </div>
        </div>

        {/* Movie Header */}
        <div className="relative z-10 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
              {/* Movie Poster */}
              <div className="flex-shrink-0 mx-auto lg:mx-0">
                <div className="relative">
                  {getBestPosterSource() ? (
                    <OptimizedImage
                      src={getBestPosterSource()}
                      alt={movie.title}
                      className="w-80 h-120 rounded-xl shadow-2xl"
                      loading="eager"
                      placeholder={<PosterFallback />}
                    />
                  ) : (
                    <PosterFallback />
                  )}
                </div>
              </div>

              {/* Movie Info */}
              <div className="flex-1 text-center lg:text-left">
                <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                  {movie.title}
                </h1>
                
                {movie.tagline && (
                  <p className="text-xl text-gray-300 mb-6 italic">{movie.tagline}</p>
                )}

                {/* Movie Meta */}
                <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 mb-6 text-gray-300">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <span>{new Date(movie.release_date).getFullYear()}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <span>{formatRuntime(movie.runtime)}</span>
                  </div>

                  {/* Genres */}
                  <div className="flex flex-wrap gap-2">
                    {getGenreNames(movie.genres).slice(0, 3).map((genre, index) => (
                      <span key={`${movie.id}-genre-${index}`} className="px-3 py-1 bg-primary-600/20 text-primary-300 rounded-full text-sm">
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Ratings */}
                {(() => {
                  const ratings = getRatingData();
                  if (!ratings.imdb && !ratings.rt && !ratings.tmdb) return null;
                  
                  return (
                    <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 mb-6">
                      {ratings.imdb && (
                        <div className="flex items-center space-x-2 bg-yellow-600/20 px-4 py-2 rounded-lg">
                          <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-yellow-400 font-bold text-lg">{ratings.imdb}</span>
                          <span className="text-yellow-300 font-medium">IMDB</span>
                        </div>
                      )}
                      
                      {ratings.rt && (
                        <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                          ratings.rt >= 60 ? 'bg-red-600/20' : 'bg-green-600/20'
                        }`}>
                          <svg className={`w-5 h-5 ${ratings.rt >= 60 ? 'text-red-400' : 'text-green-400'}`} 
                               fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                          <span className={`font-bold text-lg ${
                            ratings.rt >= 60 ? 'text-red-400' : 'text-green-400'
                          }`}>
                            {ratings.rt}%
                          </span>
                          <span className="text-gray-300 font-medium">Rotten Tomatoes</span>
                        </div>
                      )}
                      
                      {ratings.tmdb && (
                        <div className="flex items-center space-x-2 bg-blue-600/20 px-4 py-2 rounded-lg">
                          <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-blue-400 font-bold text-lg">{ratings.tmdb}</span>
                          <span className="text-blue-300 font-medium">TMDB</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Availability Warning — hide until TV3 (and other) external lookups finish */}
                {streamingLookupResolved && streamingProviders && (
                  !streamingProviders.flatrate?.length && 
                  !streamingProviders.buy?.length && 
                  !streamingProviders.rent?.length && 
                  !streamingProviders.free?.length &&
                  !hasExternalAvailability
                ) && (
                  <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center lg:justify-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <div className="text-center lg:text-left">
                        <h3 className="text-amber-200 font-medium">{t('notAvailable')}</h3>
                        <p className="text-amber-300/80 text-sm mt-1">
                          {t('notAvailableDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Overview */}
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold mb-3">{t('description')}</h2>
                  <p className="text-gray-300 leading-relaxed text-lg">
                    {movie.overview || t('noDescription')}
                  </p>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-center lg:text-left">
                  {movie.budget > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{t('budget')}</h3>
                      <p className="text-gray-300">${movie.budget.toLocaleString()}</p>
                    </div>
                  )}
                  
                  {movie.revenue > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{t('revenue')}</h3>
                      <p className="text-gray-300">${movie.revenue.toLocaleString()}</p>
                    </div>
                  )}
                  
                  {movie.production_companies?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{t('studio')}</h3>
                      <p className="text-gray-300">
                        {movie.production_companies.map(company => company.name).join(', ')}
                      </p>
                    </div>
                  )}
                  
                  {movie.production_countries?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{t('country')}</h3>
                      <p className="text-gray-300">
                        {movie.production_countries.map(country => country.name).join(', ')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center lg:justify-start">
                  <MediaActionButtons 
                    item={movie} 
                    mediaType="movie"
                    onRequireAuth={handleRequireAuth}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
        onRegistrationSuccess={() => {
          setShowAuthModal(false);
        }}
      />
    </>
  );
});

MovieHeader.displayName = 'MovieHeader';

export default MovieHeader; 